import { SidebarList } from "@/lib/Components/SideBarList/SideBarList";
import { ArrowDown10, ArrowLeftRightIcon } from "lucide-react";
import { formatDate } from "@/lib/Formatters/formatDate";
import {
  BuildingOfficeIcon,
  CalendarDaysIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { useInfiniteGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  BookingListItem,
  bookingListQuery,
} from "@/app/Bookings/GraphQL/bookingListQuery";
import { getBookingListQueryVariables } from "@/app/Bookings/Utils/getBookingListQueryVariables";
import { keepPreviousData } from "@tanstack/react-query";
import { Badge } from "@/components/catalyst/badge";
import { bookingStatusColorMap } from "@/app/Bookings/Utils/bookingStatusColorMap";
import { formatDateTimeSinceNow } from "@/lib/Formatters/formatDateTimeSinceNow";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { MultiSelectFilter } from "@/lib/Components/DataTable/Filters/MultiSelectFilter";
import { SupplierFilter } from "@/lib/Components/DataTable/Filters/SupplierFilter";
import {
  SelectFilter,
  SelectFilterOption,
} from "@/lib/Components/DataTable/Filters/SelectFilter";
import { BookingRelocationMatchType, BookingStatus } from "@/gql/graphql";
import { useTranslations } from "use-intl";
import { Highlighter } from "@/lib/Components/Highlighter";
import { SupplierIcon } from "@/app/Suppliers/Components/SupplierIcon";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "@/components/catalyst/dropdown";
import { CheckIcon } from "@heroicons/react/20/solid";
const matchTypeOptions: SelectFilterOption<BookingRelocationMatchType>[] = [
  {
    label: "Exact",
    value: BookingRelocationMatchType.Exact,
  },
  {
    label: "Similar",
    value: BookingRelocationMatchType.Similar,
  },
  {
    label: "Broad",
    value: BookingRelocationMatchType.Broad,
  },
];

export type BookingSortOption =
  | "newest"
  | "recently_cancelled"
  | "recently_confirmed";

const sortOptions: SelectFilterOption<BookingSortOption>[] = [
  {
    label: "Newest",
    value: "newest",
  },
  {
    label: "Recently Cancelled",
    value: "recently_cancelled",
  },
  {
    label: "Recently Confirmed",
    value: "recently_confirmed",
  },
];

export function BookingListScreen() {
  const { isAdmin } = useTenant();
  const [search, setSearch] = useState("");
  const t = useTranslations("booking");
  const [selectedStatus, setSelectedStatus] = useState<BookingStatus[]>([]);
  const [hasMatches, setHasMatches] =
    useState<BookingRelocationMatchType | null>(null);
  const [sortBy, setSortBy] = useState<BookingSortOption>("newest");
  const [showSortMenu, setShowSortMenu] = useState(false);
  const { supplierId: globalSupplierId } = useGlobalSupplier();
  const [filterSupplierId, setFilterSupplierId] = useState<string | null>(null);

  const supplierId = globalSupplierId ?? filterSupplierId;

  const queryVariables = getBookingListQueryVariables(
    {
      bookingStatus: selectedStatus,
      search,
      hasMatches,
      orderByOption: sortBy,
    },
    supplierId,
  );

  const { data, fetchNextPage, isFetching, isFetchingNextPage } =
    useInfiniteGqlQuery(bookingListQuery, queryVariables, {
      placeholderData: keepPreviousData,
      initialPageParam: {
        page: 1,
      },
      getNextPageParam: (lastPage) => {
        return lastPage.bookings.paginatorInfo.hasMorePages
          ? { page: lastPage.bookings.paginatorInfo.currentPage + 1 }
          : undefined;
      },
    });

  const items = data?.pages.flatMap((item) => item.bookings.data) ?? [];
  const totalCount = data?.pages.at(0)?.bookings.paginatorInfo.total ?? 0;

  return (
    <SidebarList
      items={items}
      totalCount={totalCount}
      isFetching={isFetching}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      createRoute={
        isAdmin
          ? "/$tenant/$orgId/supplier/{-$supplierId}/bookings/create"
          : undefined
      }
      listRoute={"/$tenant/$orgId/supplier/{-$supplierId}/bookings"}
      search={search}
      onSearchChange={setSearch}
      itemNode={(item) => <BookingItem model={item} search={search} />}
      linkProps={(item) => ({
        params: (prev) => ({
          ...prev,
          bookingId: item.id,
        }),
      })}
      filters={
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-wrap gap-2">
            <MultiSelectFilter
              label="Status"
              options={Object.values(BookingStatus).map((status) => ({
                label: t(`status.${status}`),
                value: status,
              }))}
              selected={selectedStatus}
              onChange={setSelectedStatus}
            />
            {globalSupplierId ? null : (
              <SupplierFilter
                label={t("supplierFilter")}
                selected={filterSupplierId}
                onChange={setFilterSupplierId}
              />
            )}
            {isAdmin ? (
              <SelectFilter
                label={t("hasMatches")}
                options={matchTypeOptions}
                selected={hasMatches}
                onChange={setHasMatches}
              />
            ) : null}
          </div>

          <div className="relative flex items-center">
            <Dropdown>
              <DropdownButton as="button">
                <ArrowDown10
                  data-slot="icon"
                  className="size-4 text-gray-500"
                />
              </DropdownButton>
              <DropdownMenu>
                {sortOptions.map((option) => (
                  <DropdownItem
                    key={option.value}
                    onClick={() => {
                      setSortBy(option.value);
                    }}
                  >
                    {sortBy === option.value ? <CheckIcon /> : null}
                    <DropdownLabel>{option.label}</DropdownLabel>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>

            {showSortMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowSortMenu(false)}
                />
                <div className="absolute right-0 z-20 mt-2 w-48 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      className={`block w-full px-4 py-2 text-left text-sm ${
                        sortBy === option.value
                          ? "bg-gray-100 text-gray-900"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={() => {
                        setSortBy(option.value);
                        setShowSortMenu(false);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      }
    />
  );
}

function BookingItem({
  model,
  search,
}: {
  model: BookingListItem;
  search: string;
}) {
  const t = useTranslations("booking");
  const { hasSupplier } = useGlobalSupplier();

  return (
    <div className="flex justify-between space-x-2">
      <div className="space-y-1.5 truncate">
        <p className="truncate text-sm font-semibold text-gray-900 group-hover:text-blue-700">
          <Highlighter searchString={search}>{model.reference}</Highlighter>
        </p>
        {!hasSupplier ? (
          <p className="flex items-center truncate text-xs text-gray-600">
            <SupplierIcon
              model={model.supplier}
              className="mr-1.5 size-3.5 shrink-0 rounded-sm"
              fallback={
                <BuildingOfficeIcon className="mr-1.5 inline h-3.5 w-3.5 shrink-0 text-gray-500" />
              }
            />
            <Highlighter searchString={search}>
              {model.supplier.name}
            </Highlighter>
          </p>
        ) : null}

        <p className="flex items-center truncate text-xs text-gray-600">
          <UserIcon className="mr-1.5 inline h-3.5 w-3.5 shrink-0 text-gray-500" />
          <Highlighter searchString={search}>{model.name}</Highlighter>
        </p>
        <p className="flex items-center truncate text-xs text-gray-600">
          <ArrowLeftRightIcon className="mr-1.5 inline h-3.5 w-3.5 shrink-0 text-gray-500" />
          <Highlighter searchString={search}>
            {`${model.departureOffice.name} - ${model.deliveryOffice.name}`}
          </Highlighter>
        </p>
        <p className="flex items-center truncate text-xs text-gray-600">
          <CalendarDaysIcon className="mr-1.5 inline h-3.5 w-3.5 shrink-0 text-gray-500" />
          {`${formatDate(model.depart_at)} - ${formatDate(model.deliver_at)}`}
        </p>
        <p>
          <time
            dateTime={model.created_at}
            className="text-xs italic text-gray-500"
          >
            {formatDateTimeSinceNow(model.created_at)}
          </time>
        </p>
      </div>

      <div className="mt-1 min-w-0 shrink-0 self-start">
        <Badge
          color={bookingStatusColorMap[model.status]}
          className="px-2 py-0.5 text-xs capitalize"
        >
          {t(`statusShortLabel.${model.status}`)}
        </Badge>
      </div>
    </div>
  );
}
